/**
 * WCAG 2.1 relative luminance and contrast helpers for hex colors.
 */

/**
 * @param {string | null | undefined} value
 * @returns {string | null} lowercase #rrggbb or null
 */
export function normalizeThemeHex(value) {
  if (!value || typeof value !== 'string') return null;
  let h = value.trim();
  if (!h.startsWith('#')) h = `#${h}`;
  if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(h)) return null;
  if (h.length === 4) {
    const [, r, g, b] = h;
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return h.toLowerCase();
}

/**
 * @param {string} hex — #rrggbb
 * @returns {{ r: number, g: number, b: number } | null}
 */
function hexToRgb(hex) {
  const n = normalizeThemeHex(hex);
  if (!n || n.length !== 7) return null;
  const r = parseInt(n.slice(1, 3), 16);
  const g = parseInt(n.slice(3, 5), 16);
  const b = parseInt(n.slice(5, 7), 16);
  if ([r, g, b].some((x) => Number.isNaN(x))) return null;
  return { r, g, b };
}

/**
 * sRGB channel to linear light (WCAG).
 * @param {number} channel — 0–255
 */
function channelToLinear(channel) {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/**
 * WCAG 2.1 relative luminance for sRGB hex.
 * @param {string} hex
 * @returns {number | null}
 */
export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = channelToLinear(rgb.r);
  const g = channelToLinear(rgb.g);
  const b = channelToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * WCAG contrast ratio (1–21).
 * @param {string} hexA
 * @param {string} hexB
 * @returns {number | null}
 */
export function contrastRatio(hexA, hexB) {
  const L1 = relativeLuminance(hexA);
  const L2 = relativeLuminance(hexB);
  if (L1 == null || L2 == null) return null;
  const lighter = Math.max(L1, L2);
  const darker = Math.min(L1, L2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * If preferred foreground on background meets minRatio, returns normalized preferred;
 * otherwise returns #000000 or #ffffff (whichever contrasts more with background).
 *
 * @param {string | null | undefined} backgroundHex
 * @param {string | null | undefined} preferredForegroundHex
 * @param {{ minRatio?: number }} [opts]
 * @returns {string | null} normalized hex or null if inputs unusable
 */
export function pickAccessibleForeground(backgroundHex, preferredForegroundHex, opts = {}) {
  const minRatio = opts.minRatio ?? 4.5;
  const bg = normalizeThemeHex(backgroundHex);
  const preferred = normalizeThemeHex(preferredForegroundHex);

  if (!bg) return preferred;

  if (preferred) {
    const ratio = contrastRatio(preferred, bg);
    if (ratio != null && ratio >= minRatio) {
      return preferred;
    }
  }

  const black = '#000000';
  const white = '#ffffff';
  const rBlack = contrastRatio(black, bg);
  const rWhite = contrastRatio(white, bg);
  if (rBlack == null || rWhite == null) return preferred ?? black;
  return rBlack >= rWhite ? black : white;
}

/**
 * Prefer API foreground on surface when contrast passes; otherwise pick accessible black/white.
 * Keeps high-contrast themes unchanged (e.g. white icons on dark `#003366`).
 *
 * @param {string | null | undefined} surfaceHex — e.g. `buttonBackground`
 * @param {string | null | undefined} preferredForegroundHex — e.g. `buttonColor`
 * @param {{ minRatio?: number }} [opts]
 * @returns {string | null} normalized `#rrggbb`, or null if no usable foreground
 */
export function resolveAccessibleForegroundForSurface(
  surfaceHex,
  preferredForegroundHex,
  opts = {}
) {
  const minRatio = opts.minRatio ?? 4.5;
  const bg = normalizeThemeHex(surfaceHex);
  const preferred = normalizeThemeHex(preferredForegroundHex);
  if (!preferred) return null;
  if (!bg) return preferred;
  return pickAccessibleForeground(bg, preferred, { minRatio }) ?? preferred;
}

/**
 * Linear mix between two hex colors (sRGB).
 * @param {string} hexA
 * @param {string} hexB
 * @param {number} t — 0 = A, 1 = B
 * @returns {string | null} #rrggbb
 */
export function mixHex(hexA, hexB, t) {
  const A = hexToRgb(hexA);
  const B = hexToRgb(hexB);
  if (!A || !B) return null;
  const clamp = (n) => Math.max(0, Math.min(255, n));
  const r = clamp(Math.round(A.r + (B.r - A.r) * t));
  const g = clamp(Math.round(A.g + (B.g - A.g) * t));
  const b = clamp(Math.round(A.b + (B.b - A.b) * t));
  const h = (n) => n.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

/**
 * Space-separated `r g b` for `--rtk-colors-background-800`-style tokens.
 * @param {string} hex — #rrggbb
 * @returns {string | null}
 */
export function hexToRgbSpaceSeparated(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return `${rgb.r} ${rgb.g} ${rgb.b}`;
}

/**
 * Prefer theme `buttonBackground` as hover fill; if `foregroundHex` on that surface is below
 * `minRatio`, blend toward white/black until contrast passes, else `fallbackRgb`.
 *
 * @param {string | null | undefined} preferredBackgroundHex — e.g. `buttonBackground`
 * @param {string | null | undefined} foregroundHex — e.g. `buttonColor` or `headerColor`
 * @param {{ minRatio?: number, fallbackRgb?: string }} [opts]
 */
export function resolveAccessibleHoverBackgroundRgb(
  preferredBackgroundHex,
  foregroundHex,
  opts = {}
) {
  const minRatio = opts.minRatio ?? 4.5;
  const fallbackRgb = opts.fallbackRgb ?? '154 154 154';

  const bg = normalizeThemeHex(preferredBackgroundHex);
  if (!bg) return fallbackRgb;

  const fg = normalizeThemeHex(foregroundHex);
  const rgbStrForHex = (h) => hexToRgbSpaceSeparated(h) ?? fallbackRgb;

  if (!fg) {
    return rgbStrForHex(bg);
  }

  let ratio = contrastRatio(fg, bg);
  if (ratio != null && ratio >= minRatio) {
    return rgbStrForHex(bg);
  }

  for (const toward of ['#ffffff', '#000000']) {
    for (let step = 1; step <= 10; step += 1) {
      const t = step / 10;
      const mixed = mixHex(bg, toward, t);
      if (!mixed) continue;
      ratio = contrastRatio(fg, mixed);
      if (ratio != null && ratio >= minRatio) {
        return rgbStrForHex(mixed);
      }
    }
  }

  return fallbackRgb;
}

/** Light hover fills for overflow menu rows (popover reads as white). */
const MENU_ROW_LIGHT_HOVER_HEXES = [
  '#ffffff',
  '#f8f8f8',
  '#f0f0f0',
  '#e8e8e8',
  '#e0e0e0',
  '#d8d8d8',
  '#d0d0d0',
  '#c8c8c8',
  '#c0c0c0',
];

/**
 * Hover fill for `[slot="more-elements"]` rows: labels follow header/text; `[brand-icon]` uses
 * brand (≈ `buttonBackground`). Prefer **light** surfaces so saturated foregrounds stay readable —
 * blending theme `buttonBackground` toward black produced dark hovers with unchanged blue icons.
 *
 * Does not use `buttonColor` (often white for primary buttons); that would falsely fail light hovers.
 *
 * @param {object | null | undefined} themeDetails — from `getMeetingThemeDetails`
 * @param {{ minRatio?: number, fallbackRgb?: string }} [opts]
 * @returns {string} space-separated `r g b`
 */
export function resolveAccessibleMenuRowHoverRgb(themeDetails, opts = {}) {
  const minRatio = opts.minRatio ?? 4.5;
  const fallbackRgb = opts.fallbackRgb ?? '154 154 154';

  const preset = themeDetails?.presetTheme ?? {};

  const fgCandidates = [
    themeDetails?.headerColor ?? preset.headerColor,
    themeDetails?.buttonBackground ?? preset.buttonBackground,
  ]
    .map((x) => normalizeThemeHex(x))
    .filter(Boolean);

  const unique = [...new Set(fgCandidates)];

  if (unique.length === 0) return fallbackRgb;

  const allPass = (bgHex) =>
    unique.every((fg) => {
      const r = contrastRatio(fg, bgHex);
      return r != null && r >= minRatio;
    });

  for (const bg of MENU_ROW_LIGHT_HOVER_HEXES) {
    if (allPass(bg)) {
      return hexToRgbSpaceSeparated(bg) ?? fallbackRgb;
    }
  }

  for (let step = 1; step <= 20; step += 1) {
    const t = step / 20;
    const mixed = mixHex('#ffffff', '#000000', t * 0.45);
    if (mixed && allPass(mixed)) {
      return hexToRgbSpaceSeparated(mixed) ?? fallbackRgb;
    }
  }

  return fallbackRgb;
}
